import { Injectable, inject, signal } from '@angular/core';
import { Firestore, doc, updateDoc, arrayUnion } from '@angular/fire/firestore';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { environment } from '../../environments/environment';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class NotificationService extends FirebaseAwareService {
  private firestore = inject(Firestore);

  readonly permissionStatus = signal<NotificationPermission | 'unknown'>('unknown');
  readonly foregroundMessage = signal<{ title: string; body: string } | null>(null);

  private nativeInitialized = false;

  constructor() {
    super();
    if (typeof window !== 'undefined' && 'Notification' in window) {
      this.permissionStatus.set(Notification.permission);
    }
  }

  /** Called from MenuComponent once after auth is confirmed. */
  async init(uid: string): Promise<void> {
    if (typeof window === 'undefined') return;

    if (Capacitor.isNativePlatform()) {
      await this.#initNative(uid);
    } else {
      await this.#initWeb(uid);
    }
  }

  // ─── Native (Capacitor Android / iOS) ────────────────────────────────────

  async #initNative(uid: string): Promise<void> {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      if (!this.nativeInitialized) {
        this.nativeInitialized = true;

        // Android 8+ requires a channel with HIGH importance for heads-up (popup) notifications
        await PushNotifications.createChannel({
          id: 'tfr_default',
          name: 'TimeFlexRent',
          description: 'Notificaciones de reservas y actividad',
          importance: 4,   // HIGH — shows as heads-up popup with sound
          visibility: 1,   // PUBLIC
          vibration: true,
          lights: true,
          lightColor: '#006BFF',
          sound: 'default',
        });

        PushNotifications.addListener('pushNotificationReceived', notification => {
          const title = notification.title ?? 'TimeFlexRent';
          const body  = notification.body  ?? '';
          this.foregroundMessage.set({ title, body });
          setTimeout(() => this.foregroundMessage.set(null), 6000);
        });

        PushNotifications.addListener('registrationError', err => {
          console.warn('[FCM] Native registration error:', JSON.stringify(err));
        });
      }

      const status = await PushNotifications.checkPermissions();
      let receive = status.receive;

      if (receive === 'prompt') {
        const req = await PushNotifications.requestPermissions();
        receive = req.receive;
      }

      this.permissionStatus.set(receive === 'granted' ? 'granted' : 'denied');
      if (receive !== 'granted') return;

      // register() triggers the 'registration' event with the FCM token
      await PushNotifications.register();

      PushNotifications.addListener('registration', async token => {
        try {
          const userRef = this.inCtx(() => doc(this.firestore, 'users', uid));
          await this.inCtx(() => updateDoc(userRef, { fcmTokens: arrayUnion(token.value) }));
          console.log('[FCM] Native token saved:', token.value.slice(0, 20) + '…');
        } catch (err) {
          console.warn('[FCM] Error saving native token:', err);
        }
      });
    } catch (err) {
      console.warn('[FCM] Native init failed:', err);
    }
  }

  // ─── Web (PWA / browser) ─────────────────────────────────────────────────

  async #initWeb(uid: string): Promise<void> {
    const { isSupported, getMessaging, getToken, onMessage } = await import('firebase/messaging');
    const supported = await isSupported();
    if (!supported) return;

    const messaging = this.inCtx(() => getMessaging());

    this.inCtx(() =>
      onMessage(messaging, payload => {
        const title = payload.notification?.title ?? 'TimeFlexRent';
        const body  = payload.notification?.body  ?? '';
        this.foregroundMessage.set({ title, body });
        setTimeout(() => this.foregroundMessage.set(null), 6000);
      }),
    );

    if (!('Notification' in window) || Notification.permission === 'denied') return;
    await this.#getAndSaveWebToken(uid, messaging, getToken);
  }

  async requestPermission(uid: string): Promise<void> {
    if (typeof window === 'undefined') return;
    if (Capacitor.isNativePlatform()) {
      await this.#initNative(uid);
      return;
    }
    const { isSupported, getMessaging, getToken } = await import('firebase/messaging');
    const supported = await isSupported();
    if (!supported) return;
    const messaging = this.inCtx(() => getMessaging());
    await this.#getAndSaveWebToken(uid, messaging, getToken);
  }

  async #getAndSaveWebToken(
    uid: string,
    messaging: Awaited<ReturnType<typeof import('firebase/messaging').getMessaging>>,
    getToken: typeof import('firebase/messaging').getToken,
  ): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      this.permissionStatus.set(permission);
      if (permission !== 'granted') return;

      const token = await this.inCtx(() =>
        getToken(messaging, { vapidKey: environment.fcmVapidKey }),
      );
      if (!token) return;

      const userRef = this.inCtx(() => doc(this.firestore, 'users', uid));
      await this.inCtx(() => updateDoc(userRef, { fcmTokens: arrayUnion(token) }));
    } catch (err) {
      console.warn('[FCM] No se pudo obtener el token de notificaciones:', err);
    }
  }
}
